import { useState } from 'react';
import { Button, InputNumber, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { rowRole, type HugimPercentRow } from '@salaries/shared';
import { listHugimCourses } from '../../api/hugim';
import { PercentSumWarning } from './PercentSumWarning';

/** One course's percentages, one per role; null = not set for that role. */
export interface CourseRolePercent {
  hug_key: string;
  primary: number | null;
  secondary: number | null;
}

/** Fold role-tagged rows into one entry per course. */
export function byCourse(rows: HugimPercentRow[]): CourseRolePercent[] {
  const out = new Map<string, CourseRolePercent>();
  for (const r of rows) {
    const e = out.get(r.hug_key) ?? { hug_key: r.hug_key, primary: null, secondary: null };
    e[rowRole(r)] = r.percent;
    out.set(r.hug_key, e);
  }
  return [...out.values()];
}

/** "ראשי 50% / משני 20%", an unset role as "-". */
export function rolePercentLabel(e: { primary: number | null; secondary: number | null }): string {
  const pct = (v: number | null) => (v == null ? '-' : `${v}%`);
  return `ראשי ${pct(e.primary)} / משני ${pct(e.secondary)}`;
}

interface Props {
  /** The courses that already carry their own percentage. */
  rows: CourseRolePercent[];
  onAdd: (hugKey: string, primary: number | null, secondary: number | null, done: () => void) => void;
  onRemove: (hugKey: string) => void;
  adding: boolean;
}

// "אחוז לפי חוג" — a per-course percentage list: pick a course, give it a %
// for the primary and/or the secondary staff member, add; remove sends the
// course back to the defaults. A course appears once (it drops out of the
// picker as soon as it has a row). Shared by the course percentages and the
// מוצרים נלווים ones; the caller owns saving.
export function CoursePercentList({ rows, onAdd, onRemove, adding }: Props) {
  const coursesQ = useQuery({ queryKey: ['hugim-courses'], queryFn: listHugimCourses });
  const [hugSel, setHugSel] = useState<string | undefined>();
  const [primary, setPrimary] = useState<number | null>(50);
  const [secondary, setSecondary] = useState<number | null>(null);

  const courseName = (key: string) =>
    coursesQ.data?.find((c) => c.hug_key === key)?.hug_name ?? key;
  const hugOptions = (coursesQ.data ?? [])
    .filter((c) => !rows.some((o) => o.hug_key === c.hug_key))
    .map((c) => ({ value: c.hug_key, label: c.hug_name }));

  return (
    <div>
      <div className="text-label font-medium text-main-text mb-2">אחוז לפי חוג</div>
      <div className="flex items-end gap-2">
        <Select
          showSearch
          placeholder="בחר חוג"
          style={{ flex: 1 }}
          value={hugSel}
          onChange={setHugSel}
          options={hugOptions}
          optionFilterProp="label"
          loading={coursesQ.isLoading}
        />
        <RolePercentInput label="ראשי" value={primary} onChange={setPrimary} />
        <RolePercentInput label="משני" value={secondary} onChange={setSecondary} />
        <Button
          type="primary"
          disabled={!hugSel || (primary == null && secondary == null)}
          loading={adding}
          onClick={() =>
            hugSel &&
            onAdd(hugSel, primary, secondary, () => {
              setHugSel(undefined);
              setSecondary(null);
            })
          }
        >
          הוסף
        </Button>
      </div>
      <PercentSumWarning primary={primary} secondary={secondary} />
      {rows.length > 0 && (
        <div className="border rounded mt-2">
          {rows.map((o) => (
            <div
              key={o.hug_key}
              className="flex items-center justify-between px-3 py-2 border-b last:border-b-0"
            >
              <span className="text-main-text">{courseName(o.hug_key)}</span>
              <span className="flex items-center gap-3">
                <span className="text-main-text">{rolePercentLabel(o)}</span>
                <Button size="small" danger type="text" onClick={() => onRemove(o.hug_key)}>
                  הסר
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A labelled 0–100 field for one role, blank allowed. */
export function RolePercentInput({
  label,
  value,
  onChange,
  size,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  size?: 'small';
}) {
  return (
    <div className="flex flex-col">
      <span className="text-small text-secondary-text mb-1">{label}</span>
      <InputNumber
        min={0}
        max={100}
        dir="ltr"
        size={size}
        style={{ width: 90 }}
        value={value ?? undefined}
        onChange={(v) => onChange(v == null ? null : Number(v))}
      />
    </div>
  );
}
